import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, where, 
  doc, updateDoc, deleteDoc, Timestamp, orderBy 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Dailyexpense() {
  const [expenses, setExpenses] = useState([]);
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // UI State
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form State
  const [headId, setHeadId] = useState("");
  const [amount, setAmount] = useState("");
  const [payMode, setPayMode] = useState("Cash");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState("");

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  // 1. Fetch Expense Heads (For Dropdown)
  useEffect(() => {
    if (!userId) return;
    const qH = query(collection(db, "expense_heads"), where("userId", "==", userId));
    const unsub = onSnapshot(qH, (snap) => setHeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [userId]);

  // 2. Fetch Daily Expenses
  useEffect(() => {
    if (!userId) return;
    const qE = query(collection(db, "daily_expenses"), where("userId", "==", userId), orderBy("date", "desc"));
    const unsub = onSnapshot(qE, (snap) => setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [userId]);

  const openModal = (exp = null) => {
    if (exp) {
      setEditId(exp.id);
      setHeadId(exp.headId);
      setAmount(exp.amount);
      setPayMode(exp.payMode);
      setDate(exp.date);
      setDescription(exp.description);
    } else {
      setEditId(null);
      setHeadId("");
      setAmount("");
      setPayMode("Cash");
      setDate(new Date().toISOString().split('T')[0]);
      setDescription("");
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        userId, headId, amount: parseFloat(amount), payMode, date, description,
        updatedAt: Timestamp.now()
      };

      if (editId) {
        await updateDoc(doc(db, "daily_expenses", editId), data);
        Toast.fire({ icon: 'success', title: 'Expense Updated' });
      } else {
        await addDoc(collection(db, "daily_expenses"), { ...data, createdAt: Timestamp.now() });
        Toast.fire({ icon: 'success', title: 'Expense Added' });
      }
      setShowModal(false);
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally { setLoading(false); }
  };

  const deleteExpense = async (id) => {
    const confirm = await Swal.fire({ title: 'Delete Expense?', icon: 'warning', showCancelButton: true });
    if (confirm.isConfirmed) {
      await deleteDoc(doc(db, "daily_expenses", id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  // --- Search & Pagination Logic ---
  const filteredData = expenses.filter(exp => {
    const headName = heads.find(h => h.id === exp.headId)?.headName || "";
    return headName.toLowerCase().includes(searchTerm.toLowerCase()) || 
           exp.description.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded border-left-danger">
          <h4 className="m-0 font-weight-bold text-navy"><i className="fas fa-wallet mr-2 text-danger"></i>Daily Expenses</h4>
          <button className="btn btn-danger shadow-sm" onClick={() => openModal()}><i className="fas fa-plus mr-1"></i> Add Expense</button>
        </div>
      </section>

      <section className="content px-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold">Expense Logs</h6>
            <input 
              type="text" className="form-control form-control-sm w-25" 
              placeholder="Search by category or note..." 
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="card-body p-0">
            <table className="table table-hover mb-0">
              <thead className="bg-light small uppercase">
                <tr>
                  <th className="text-center">Sr.</th>
                  <th>Date</th>
                  <th>Head / Category</th>
                  <th>Pay Mode</th>
                  <th>Description</th>
                  <th className="text-right">Amount</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((exp, i) => (
                  <tr key={exp.id}>
                    <td className="text-center text-muted">{indexOfFirstItem + i + 1}</td>
                    <td>{exp.date}</td>
                    <td className="font-weight-bold">{heads.find(h => h.id === exp.headId)?.headName || 'N/A'}</td>
                    <td><span className="badge badge-info">{exp.payMode}</span></td>
                    <td className="small">{exp.description || '--'}</td>
                    <td className="text-right font-weight-bold text-danger">₹ {exp.amount.toLocaleString()}</td>
                    <td className="text-center">
                      <button className="btn btn-sm text-primary mr-2" onClick={() => openModal(exp)}><i className="fas fa-edit"></i></button>
                      <button className="btn btn-sm text-danger" onClick={() => deleteExpense(exp.id)}><i className="fas fa-trash"></i></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination Footer */}
          <div className="card-footer bg-white d-flex justify-content-between">
            <small>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length}</small>
            <div>
              <button className="btn btn-xs btn-outline-dark mr-1" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>Prev</button>
              <button className="btn btn-xs btn-outline-dark" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
            </div>
          </div>
        </div>
      </section>

      {/* REACT MODAL */}
      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content animate__animated animate__fadeInDown">
            <div className="modal-header bg-navy text-white py-2">
              <h6 className="modal-title">{editId ? 'Edit Expense' : 'Add Daily Expense'}</h6>
              <button className="close text-white" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-4">
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="small font-weight-bold">Select Head</label>
                    <select className="form-control" value={headId} onChange={e => setHeadId(e.target.value)} required>
                      <option value="">-- Choose --</option>
                      {heads.map(h => <option key={h.id} value={h.id}>{h.headName}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="small font-weight-bold">Date</label>
                    <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} required />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="small font-weight-bold">Amount (₹)</label>
                    <input type="number" className="form-control" value={amount} onChange={e => setAmount(e.target.value)} required />
                  </div>
                  <div className="col-6">
                    <label className="small font-weight-bold">Pay Mode</label>
                    <select className="form-control" value={payMode} onChange={e => setPayMode(e.target.value)}>
                      <option>Cash</option><option>PhonePe/UPI</option><option>Bank Transfer</option><option>Cheque</option>
                    </select>
                  </div>
                </div>
                <label className="small font-weight-bold">Description / Note</label>
                <textarea className="form-control" rows="2" value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter details..."></textarea>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" disabled={loading} className="btn btn-danger btn-sm px-4">{loading ? 'Saving...' : 'Save Expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .text-navy { color: #0f172a !important; }
        .bg-navy { background: #0f172a !important; }
        .border-left-danger { border-left: 5px solid #dc3545; }
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050;
        }
        .custom-modal-content {
          background: white; width: 500px; border-radius: 8px; overflow: hidden;
        }
      `}} />
    </div>
  );
}

export default Dailyexpense;